import { useContext, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Backdrop,
  Box,
  CircularProgress,
  CssBaseline,
  Typography,
} from "@mui/material";

import { getCreds, PageContext } from "../contexts/PageContext";
import PageHeader from "../components/PageHeader";
import Minibar from "../components/Minibar";
import { Save, Delete, Wifi2Bar, ConnectedTv, TransferWithinAStation, PermDataSetting } from "@mui/icons-material";
import { deleteRecord, loadOrgModel, loadRecords, saveRecord, uploadBlob } from "../api/connection";
import { Robot, RobotManifest, createRobot } from "../models/Robot";
import { ProgressDialogHandle, showConfirmDialog, showProgressDialog, showSnackbar } from "../components/UniversalDialog";
import { SignalingConnection } from "../api/signaling/SignalingConnection";
import { FileSystemView } from "../components/EditRobot/FileSystemView";
import { InstallScript } from "../components/EditRobot/InstallScript";
import { TabSelector } from "../components/TabSelector";
import { Details } from "../components/EditRobot/Details";
import { Manifest } from "../components/EditRobot/Manifest";
import { isLocalHost, runningOnRobot } from "../api/host-aware";
import yaml from 'js-yaml';
import { transferToOrg } from "../utils/orgUtils";
import { UserRole } from "../models/User";
import { RobotConfigTemplate } from "../models/RobotConfigTemplate";

enum EditorMode {
  Manifest = "manifest",
  Console = "console",
  Status = "status",
  Logs = "logs",
  Code = "code",
  Settings = "settings",
}

const EditRobotPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useContext(PageContext);
  const [robot, setRobot] = useState<Robot | null>(null);
  const [signaling, setSignaling] = useState<SignalingConnection | null>(null);
  const [pmStatus, setPmStatus] = useState<any>();
  const [isConnected, setIsConnected] = useState(false);
  const [signalingIP, setSignalingIP] = useState('');
  const timerRef = useRef<any>();
  const [errors, setErrors] = useState({
    name: "",
    description: "",
    type: "",
    token: "",
  });

  useEffect(() => {
    if (pmStatus) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        setIsConnected(false);
      }, 15000);
    }
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [pmStatus]);
  useEffect(() => {
    async function fetchRobot(id: string) {
      try {
        if (!user) {
          setError("User not found.");
          return;
        }
        if (id === "new") {
          setRobot({ ...createRobot(), orgId: user.orgId });
        } else {
          const fetchedRobot = createRobot(id);
          await loadOrgModel(fetchedRobot);
          if (!fetchedRobot) {
            throw new Error("Robot not found.");
          }
          setRobot(fetchedRobot);
          const sc = new SignalingConnection({
            deviceId: fetchedRobot._id,
            token: fetchedRobot.token,
            group: "process-manager",
            baseUrl: signalingIP ? `ws://${signalingIP}:28801` : '',
            onReplace: (dgram) => {
              // console.log("Received replace command:", dgram);
              if (dgram.pmStatus) {
                dgram.pmStatus.lastUpdate = Date.now();
                setIsConnected(true);
                setPmStatus(dgram.pmStatus);
              }
            },
          });
          sc.connect();
          setSignaling(sc);
        }
      } catch (err) {
        setError("Failed to fetch robot data.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    if (id) {
      // console.log("Fetching robot with ID:", id);
      fetchRobot(id);
      return () => {
        if (signaling) {
          signaling.close();
        }
      };
    }
  }, [id, user, signalingIP]);

  const pingForSignaling = async () => {
    const handle: ProgressDialogHandle = showProgressDialog('Network Discovery', 'Looking for robot', 0);
    const addresses = ['192.168.123.18', '10.42.0.1', '192.168.86.29'];
    if (isLocalHost()) {
      addresses.push('192.168.0.169');
    }
    const progressIncrement = 100 / addresses.length;
    let progress = 0;
    for (const address of addresses) {
      try {
        progress += progressIncrement;
        handle.setProgress(Math.min(progress, 100));
        handle.updateText(`Checking ${address}`);
        console.log(`Pinging address ${address} for signaling server...`);
        const resp = await fetch(`http://${address}:28800/functions/health_check`, {
          signal: AbortSignal.timeout(3000)
        });
        if (resp.ok) {
          const respJson = await resp.json();
          if (respJson.status === 'ok') {
            setSignalingIP(address);
            break;
          }
        }
      } catch { }
    }
    handle.onComplete(true, '');
  }

  const saveRobot = async (record: Robot) => {
    try {
      const parsed = yaml.load(robot?.yamlContent || '');
    } catch (err) {
      showSnackbar('Error parsing YAML content. Please check the syntax.', 'error');
      return;
    }
    setLoading(true);

    if (!validateFields()) {
      setLoading(false);
      return;
    }

    try {
      const res = await saveRecord<Robot>("robots", record);
      if (id === "new") {
        navigate("/edit-robot/" + res._id); // stay on this page
      }
    } catch (err) {
      console.error(
        `Failed to ${record._id === "new" ? "save" : "update"} robot:`,
        err
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRobot = async (record: Robot) => {
    console.log("handleDeleteRobot");
    if (!record._id) {
      console.error("Robot ID is missing. Cannot delete.");
      return;
    }

    try {
      console.log("Deleting robot:", record);
      const res = await deleteRecord<Robot>("robots", record);
      navigate("/robots");
    } catch (err) {
      console.error("Failed to delete robot:", err);
    }
  };

  async function handleDeleteClick(record: Robot) {
    console.log("Delete robot clicked");
    const resp = await showConfirmDialog("Are you sure?", "Delete Robot");
    console.log("Response from confirm dialog:", resp);
    if (!resp) {
      return;
    }
    setLoading(true);
    try {
      await handleDeleteRobot(record);
    } catch (err) {
      console.error("Error deleting robot:", err);
      // setError("Failed to delete robot.");
    } finally {
      setLoading(false);
    }
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  const validateFields = () => {
    const newErrors = {
      name: !robot?.name ? "Name is required" : "",
      description: "",
      type: !robot?.type ? "Type is required" : "",
      token: "",
    };

    setErrors(newErrors);

    return Object.values(newErrors).every((error) => error === "");
  };

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            m: 0,
            backgroundColor: "#212121",
            color: "#ECECEC",
            minHeight: "100vh",
          }}
        >
          <Backdrop
            open={loading}
            sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
          <PageHeader
            title={`Edit Robot: ${robot?.name || "New Robot"}`} 
            rightComponent={[
              <Minibar
                minibarItems={
                  id === "new"
                    ? [
                      {
                        text: "Save New Robot",
                        icon: <Save />,
                        disabled: runningOnRobot(),
                        onClick: () => robot && saveRobot(robot),
                      },
                    ]
                    : [
                      {
                        text: 'Local mode',
                        icon: signalingIP ? <ConnectedTv /> : <Wifi2Bar />,
                        onClick: pingForSignaling
                      },
                      {
                        text: "Update Robot",
                        icon: <Save />,
                        onClick: () => robot && saveRobot(robot),
                      },
                      {
                        text: "Save as Template",
                        disabled: user?.role !== UserRole.SystemAdmin,
                        icon: <PermDataSetting />,
                        onClick: async () => {
                          if (!robot) return;
                          const template = yaml.load(robot.yamlContent || '') as RobotManifest;
                          for(const orgId of template.orgs || []) {
                            const orgTemplates = await loadRecords<RobotConfigTemplate>('robotconfigtemplates');
                            const existing = orgTemplates.filter(t => t.orgId === orgId && t.type === template.templateType && t.version === template.softwareVersion);
                            console.log("Existing templates to delete:", existing);
                            for(const e of existing) {
                              await deleteRecord<RobotConfigTemplate>('robotconfigtemplates', e);
                              console.log("Deleted existing template:", e);
                            }
                            const newTemplate: RobotConfigTemplate = {
                              _id: '',
                              orgId,
                              type: template.templateType || 'default',
                              version: template.softwareVersion || '1.0.0',
                              yamlContent: robot.yamlContent,
                            };
                            await saveRecord<RobotConfigTemplate>('robotconfigtemplates', newTemplate);
                          }
                        }
                      },
                      {
                        text: "Delete Robot",
                        icon: <Delete />,
                        onClick: () => robot && handleDeleteClick(robot),
                      },
                      {
                        text: 'Transfer Robot Ownership',
                        icon: <TransferWithinAStation />,
                        onClick: async () => {
                          if (robot) {
                            const success = await transferToOrg(robot);
                            if (success) {
                              showSnackbar('Robot transferred successfully', 'success');
                              navigate('/robots');
                            }
                          }
                        },
                      }
                    ]
                }
                barLength={2}
              />,
            ]}
          />
          <TabSelector
            tabs={{
              Details: {
                component: (
                  <Details robot={robot!} onChange={setRobot} errors={errors} />
                ),
              },
              Manifest: {
                component: (
                  <Manifest
                    robot={robot!}
                    onChange={setRobot}
                    errors={errors}
                  />
                ),
              },
              System: {
                component: (
                  <FileSystemView
                    robot={robot!}
                    onChange={setRobot}
                    errors={errors}
                    pmStatus={pmStatus}
                    signaling={signaling!}
                    alternateSignalingIP={signalingIP}
                  />
                ),
                disabled: !isConnected,
              },
              "Install Scripts": {
                component: (
                  <InstallScript signaling={signaling!} robot={robot!} />
                ),
              },
            }}
          />
        </Box>
      </Box>
    </>
  );
};

export default EditRobotPage;
